// Virtual net billing: the energy price of each supply, on the Tariffs tab.
//
// A supply follows the project's tariff (the default, and every project saved
// before the choice existed) or its own supplier's energy price. Only the
// energy price matters under virtual net billing: the regulated charges stay
// on all consumption and the charge for ΛΠ1-3 and losses is the same for all.
import { Box, MenuItem, TextField, Typography } from "@mui/material";
import {
  VNB_ERR_SUPPLY_PRICE,
  VNB_SUPPLY_NAME_LABEL,
  VNB_SUPPLY_PRICE_HELPER_DYNAMIC,
  VNB_SUPPLY_PRICE_HELPER_ITEMIZED,
  VNB_SUPPLY_PRICE_HELPER_TOTAL,
  VNB_SUPPLY_PRICE_LABEL,
  VNB_SUPPLY_TARIFF_BATTERY_NOTE,
  VNB_SUPPLY_TARIFF_LABEL,
  VNB_SUPPLY_TARIFF_OWN,
  VNB_SUPPLY_TARIFF_PROJECT,
  VNB_SUPPLY_TARIFF_TEXT,
  VNB_SUPPLY_TARIFF_TITLE,
  validateVirtualNetBilling,
} from "./config/virtualNetBilling";

const numberOrBlank = (value) => (value === "" ? "" : Number(value));

/** cfg: the project config; onChange(nextCfg) commits the whole config. */
export default function VirtualNetBillingSupplyTariffs({ cfg, onChange }) {
  const btm = cfg?.btm || {};
  const vnb = btm.virtualNetBilling || {};
  const supplies = Array.isArray(vnb.supplies) ? vnb.supplies : [];
  const tariffs = cfg?.feasibility?.technical?.tariffs || {};
  const dynamic = String(tariffs.billingScheme || "").trim().toLowerCase().startsWith("dynamic");
  const itemized = !dynamic && tariffs.rateMode === "itemized";
  const helper = dynamic ? VNB_SUPPLY_PRICE_HELPER_DYNAMIC
    : itemized ? VNB_SUPPLY_PRICE_HELPER_ITEMIZED : VNB_SUPPLY_PRICE_HELPER_TOTAL;
  const error = validateVirtualNetBilling(btm, cfg).vnbSupplyPrice;
  const hasBattery = String(cfg?.systemType || "").trim().toLowerCase() !== "standalone pv";

  const patchSupply = (index, partial) => onChange({
    ...cfg,
    btm: { ...btm, virtualNetBilling: { ...vnb, supplies: supplies.map((s, i) => (i === index ? { ...s, ...partial } : s)) } },
  });

  if (!supplies.length) return null;
  return (
    <Box id="tariffs-virtual-net-billing" sx={{ p: 1.5, border: "1px solid", borderColor: "success.main", borderRadius: 1.5 }}
      data-testid="vnb-supply-tariffs">
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {VNB_SUPPLY_TARIFF_TITLE}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {VNB_SUPPLY_TARIFF_TEXT}
      </Typography>
      {supplies.map((supply, index) => {
        const own = supply?.tariffMode === "own";
        return (
          <Box key={supply.id || index}
            sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1.4fr 1.4fr 1.6fr" }, gap: 1.5, alignItems: "start", mb: 1.5 }}>
            <TextField size="small" label={VNB_SUPPLY_NAME_LABEL} value={supply.name ?? ""}
              InputProps={{ readOnly: true }} inputProps={{ "data-no-translate": "" }} />
            <TextField select size="small" label={VNB_SUPPLY_TARIFF_LABEL} value={own ? "own" : "project"}
              onChange={(e) => patchSupply(index, { tariffMode: e.target.value })}
              inputProps={{ "data-testid": `vnb-supply-tariff-${index}` }}>
              <MenuItem value="project">{VNB_SUPPLY_TARIFF_PROJECT}</MenuItem>
              <MenuItem value="own">{VNB_SUPPLY_TARIFF_OWN}</MenuItem>
            </TextField>
            {own ? (
              <TextField size="small" type="number" label={VNB_SUPPLY_PRICE_LABEL}
                value={supply.energyPriceEurKwh ?? ""}
                onChange={(e) => patchSupply(index, { energyPriceEurKwh: numberOrBlank(e.target.value) })}
                error={Boolean(error) && !(Number(supply.energyPriceEurKwh) > 0 && Number(supply.energyPriceEurKwh) <= 5)}
                helperText={helper} InputLabelProps={{ shrink: true }}
                inputProps={{ min: 0, max: 5, step: "any", "data-testid": `vnb-supply-price-${index}` }} />
            ) : <Box />}
          </Box>
        );
      })}
      {error && (
        <Typography variant="caption" color="error" sx={{ display: "block" }}>
          {VNB_ERR_SUPPLY_PRICE}
        </Typography>
      )}
      {hasBattery && supplies.some((s) => s?.tariffMode === "own") && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
          {VNB_SUPPLY_TARIFF_BATTERY_NOTE}
        </Typography>
      )}
    </Box>
  );
}
